import React, { useState, useMemo, useEffect } from "react";
import { Form, FormGroup, Label, Input, Button, Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "components/Common/custom-select";
import { FaPlusCircle } from "react-icons/fa";
import TableContainer from "components/Common/TableContainer";
import { FiDownload, FiEye, FiPlusCircle } from "react-icons/fi";
import nv7 from "../../../assets/images/brands/b-nav-7.png";
import { useDispatch, useSelector } from "react-redux";
import {
  createEmployee,
  fetchEmployeeTabWiseInfo,
} from "store/actions";
import { handleDownload } from "common/fileDownload";
import CustomDatePicker from "common/CustomDatePicker";

const WorkExperience = ({ empID, authDetails, isActive }) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [employeeExpInformations, setEmployeeExpInformations] = useState([]);
  const [dateOfJoin, setdateOfJoin] = useState(null);
  const {
    employeeExpeInfo = [],
    success,
    loading,
    error,
  } = useSelector((state) => ({
    employeeExpeInfo: empID == 0 ? [] : state.EmployeeDetailsReducer?.employeeTabData?.work_exp || [],

    loading: state.employeeReducer?.loading,
    error: state.employeeReducer?.error,
    success: state.EmployeeDetailsReducer?.success
  }));

  useEffect(() => {
    if(employeeExpeInfo){
      setEmployeeExpInformations(employeeExpeInfo.experience_details)
      setdateOfJoin(employeeExpeInfo.doj)
    }
  },[employeeExpeInfo])

  const alphaNumSpecial100Regex = /^[A-Za-z0-9\s.,\-/#()]+$/;
  const alphaNumSpecial50Regex  = /^[A-Za-z0-9\s.,\-/#()]+$/;
 
  const formik = useFormik({
    initialValues: {
      companyName: "",
      fromDate: "",
      toDate: "",
      designation: "",
      reportedTo: "",
      reasonForChange: "",
      file: null,
      backgroundVerification: { label: "No", value: 2 }, // Default No
      verifiedDate: "",
      verifiedTo: "",
      verifiedVia: "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string()
    .required("Company Name is Required")
    .max(100, "Company Name must not exceed 100 characters")
    .matches(
      alphaNumSpecial100Regex,
      "Only alphabets, numerics and special characters allowed"
    ),
      fromDate: Yup.date().nullable().required("From Date is Required"),
      toDate: Yup.date().nullable().required("To Date is Required"),
        // fromDate: Yup.date()
        //   .nullable()
        //   .required("From Date is Required")
        //   .max(new Date(), "From Date cannot be in the future")
        //   .test(
        //     "beforeJoining",
        //     "From Date must be before the Date of Joining",
        //     function (value) {
        //       const { dateOfJoin } = this.options.context;
        //       return !dateOfJoin || !value || new Date(value) < new Date(dateOfJoin);
        //     }
        //   ),

        // toDate: Yup.date()
        //   .nullable()
        //   .required("To Date is Required")
        //   .max(new Date(), "To Date cannot be in the future")
        //   .test(
        //     "afterFromDate",
        //     "To Date must be after From Date",
        //     function (value) {
        //       const { fromDate } = this.parent;
        //       return !value || !fromDate || new Date(value) > new Date(fromDate);
        //     }
        //   )
        //   .test(
        //     "beforeJoining",
        //     "To Date must be before the Date of Joining",
        //     function (value) {
        //       const { dateOfJoin } = this.options.context;
        //       return !dateOfJoin || !value || new Date(value) < new Date(dateOfJoin);
        //     }
        //   ),
      designation: Yup.string()
        .required("Designation is Required")
        .max(50, "Designation must not exceed 50 characters")
        .matches(
          alphaNumSpecial50Regex,
          "Only alphabets, numerics and special characters allowed"
        ),

      reportedTo: Yup.string()
        .required("Reported To is Required")
        .max(50, "Reported To must not exceed 50 characters")
        .matches(
          alphaNumSpecial50Regex,
          "Only alphabets, numerics and special characters allowed"
        ),

      reasonForChange: Yup.string()
        .required("Reason for Change is Required")
        .max(50, "Reason for Change must not exceed 50 characters")
        .matches(
          alphaNumSpecial50Regex,
          "Only alphabets, numerics and special characters allowed"
        ),

      file: Yup.mixed()
        .test("fileRequired", "File is required", (value) => value !== null)
        .test("fileSize", "File size is too large (max 2MB)", (value) =>
          value ? value.size <= 2 * 1024 * 1024 : true
        )
        .test(
          'fileType',
          'Only JPG, JPEG, PNG, and PDF formats are allowed',
          (value) =>
            value &&
            ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'].includes(value.type)
        ),
        backgroundVerification: Yup.object().required("Background Verification is Required"),

        verifiedDate: Yup.date()
          .nullable()
          .when("backgroundVerification", {
            is: (val) => val?.value === 1,
            then: (schema) => schema.required("Verified Date is Required"),
            otherwise: (schema) => schema.nullable(),
          }),

        verifiedTo: Yup.string().when("backgroundVerification", {
          is: (val) => val?.value === 1,
          then: (schema) => schema.required("Verified To is Required"),
          otherwise: (schema) => schema.nullable(),
        }),

        verifiedVia: Yup.object().nullable().when("backgroundVerification", {
          is: (val) => val?.value === 1,
          then: (schema) => schema.required("Verified Via is Required"),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
    context: { dateOfJoin },
    onSubmit: (values) => {
      // console.log("Submitted Equipment:", values);
      const payload = {
        "exp": [
          {
            emp_id: empID,
            company_name: values.companyName,
            designation: values.designation,
            // reported_to: values.reportedTo?.value,
            reported_to: values.reportedTo,
            from_dt: values.fromDate,
            till_dt: values.toDate,
            exp_yrs: 0,
            reason_change: values.reasonForChange,
            background_verification: values.backgroundVerification?.value || 2,
            verified_date: values.backgroundVerification?.value === 1 ? values.verifiedDate : "",
            verified_to: values.backgroundVerification?.value === 1 ? values.verifiedTo : "",
            verified_via: values.backgroundVerification?.value === 1 ? values.verifiedVia?.value : 0,
          }
        ]
      };

      const formData = new FormData();
      formData.append("lst_employee_Others", JSON.stringify(payload));
      formData.append("create_by", authDetails?.user_id);
      formData.append("experience_file", values.file);

      dispatch(createEmployee(formData, 'work_exp'));
      formik.resetForm()
      setSelectedFile(null)
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => {
          const fileUrl = row.original?.file_path; // Assuming 'file' holds the URL or path
          const filePath = `${process.env.REACT_APP_IMAGE_BASE_URL}${fileUrl}`;
          const fileName = fileUrl.split("/").pop();
          return filePath ? (
            <div className="flex gap-2">
              <a href={filePath} target="_blank" rel="noopener noreferrer">
                <FiEye className="text-blue-500 cursor-pointer me-2" title="View" />
              </a>
              <a href={filePath} download={fileName} onClick={(e) => {
                e.preventDefault(); // Prevent default behavior
                handleDownload(filePath, fileName);
              }}>
                <FiDownload className="text-green-500 cursor-pointer" title="Download" />
              </a>
            </div>
          ) : (
            ""
          );
        },
      },
      { Header: "S.No", Cell: ({ row }) => employeeExpInformations.length - row.index , className: "serial-no", applyHeaderClass: true},
      { Header: "Company Name", accessor: "company_name" },
      { Header: "Designation", accessor: "designation" },
      { Header: "Reported To", accessor: "reporting_name" },
      { Header: "From Date", accessor: "from_dt" },
      { Header: "To Date", accessor: "till_dt" },
      // { Header: "Years", accessor: "exp_yrs" },

      { Header: "Reason For Change", accessor: "reason_change" },
      
    ],
    [employeeExpInformations]
  );


  // Handle File Upload
  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    setSelectedFile(file);
    formik.setFieldValue("file", file);
  };

  useEffect(() => {
    if (isActive) {
      dispatch(fetchEmployeeTabWiseInfo(empID, 'work_exp'));
    }
  }, [dispatch, isActive]);

  const [fileKey, setFileKey] = useState(Date.now());
  useEffect(() => {
      if (success) {
      formik.setFieldValue("file", null);

      // regenerate key — browser clears file input
      setFileKey(Date.now());
      }
  }, [success]);

  return (
    <>
      <Card>
        <div className="card-main-title">
          <h6 className="title">
            <img src={nv7} className="img-fluid w-auto h-auto" alt="img" />
            Work Experience
          </h6>
        </div>
        <CardBody>
          <CardTitle tag="h5" style={{ color: "#0bb2d4", fontWeight: "bold" }}></CardTitle>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Company Name <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("companyName")} maxLength={100} />
                  {formik.touched.companyName && formik.errors.companyName && (
                    <div className="text-danger">{formik.errors.companyName}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>From Date <span className="text-danger">*</span></Label>
                  {/* <Input type="date" {...formik.getFieldProps("fromDate")} /> */}
                  <CustomDatePicker
                    value={formik.values.fromDate}
                    onChange={(date) => formik.setFieldValue("fromDate", date)}
                    onBlur={() => formik.setFieldTouched("fromDate", true)}
                    maxDate={new Date(dateOfJoin)} 
                  />
                  {formik.touched.fromDate && formik.errors.fromDate && (
                    <div className="text-danger">{formik.errors.fromDate}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>To Date <span className="text-danger">*</span></Label>
                  {/* <Input type="date" {...formik.getFieldProps("toDate")} /> */}
                  <CustomDatePicker
                    value={formik.values.toDate}
                    onChange={(date) => formik.setFieldValue("toDate", date)}
                    onBlur={() => formik.setFieldTouched("toDate", true)}
                     minDate={formik.values.fromDate || null} // must be after fromDate
                     maxDate={new Date(dateOfJoin)}           // must be before joining
                  />
                  {formik.touched.toDate && formik.errors.toDate && (
                    <div className="text-danger">{formik.errors.toDate}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Designation <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("designation")} maxLength={50} />
                  {formik.touched.designation && formik.errors.designation && (
                    <div className="text-danger">{formik.errors.designation}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Reported To <span className="text-danger">*</span></Label>
                  {/* <CustomSelect
                    styles={SelectcustomStyles}
                    options={(reporting_to || []).map(bg => ({ value: bg.report_id.toString(), label: bg.report_name }))}
                    name="reportedTo"
                    value={formik.values.reportedTo || null} // Store full object
                    onChange={(selectedOption) => formik.setFieldValue("reportedTo", selectedOption)}
                    onBlur={formik.handleBlur}
                  /> */}
                  <Input type="text" {...formik.getFieldProps("reportedTo")} maxLength={50} />
                  {formik.touched.reportedTo && formik.errors.reportedTo && (
                    <div className="text-danger">{formik.errors.reportedTo}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Reason for Change <span className="text-danger">*</span></Label>
                  <Input type="text" {...formik.getFieldProps("reasonForChange")} maxLength={50} />
                  {formik.touched.reasonForChange && formik.errors.reasonForChange && (
                    <div className="text-danger">{formik.errors.reasonForChange}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Background Verification <span className="text-danger">*</span></Label>
                  <CustomSelect
                    options={[
                      { label: "Yes", value: 1 },
                      { label: "No", value: 2 },
                    ]}
                    value={formik.values.backgroundVerification}
                    onChange={(option) => {
                      formik.setFieldValue("backgroundVerification", option);
                      if (!option || option.value === 2) {
                        // Reset verification fields if No
                        formik.setFieldValue("verifiedDate", "");
                        formik.setFieldValue("verifiedTo", "");
                        formik.setFieldValue("verifiedVia", "");
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("backgroundVerification", true)}
                    className={`${
                      formik.touched.backgroundVerification &&
                      formik.errors.backgroundVerification
                        ? "is-invalid"
                        : ""
                    } hrms-select`}
                    classNamePrefix="hrms-select"
                  />
                  {formik.touched.backgroundVerification &&
                    formik.errors.backgroundVerification && (
                      <div className="text-danger">
                        {formik.errors.backgroundVerification}
                      </div>
                    )}
                </FormGroup>
              </Col>

              {/* Conditionally render verification details if Yes */}
              {formik.values.backgroundVerification?.value === 1 && (
                <>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Verified Date <span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.verifiedDate}
                        onChange={(date) => formik.setFieldValue("verifiedDate", date)}
                        onBlur={() => formik.setFieldTouched("verifiedDate", true)}
                        disableFuture={true}
                      />
                      {formik.touched.verifiedDate && formik.errors.verifiedDate && (
                        <div className="text-danger">{formik.errors.verifiedDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Verified To <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        name="verifiedTo"
                        value={formik.values.verifiedTo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`${
                          formik.touched.verifiedTo && formik.errors.verifiedTo
                            ? "is-invalid"
                            : ""
                        } form-control`}
                      />
                      {formik.touched.verifiedTo && formik.errors.verifiedTo && (
                        <div className="text-danger">{formik.errors.verifiedTo}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Verified Via <span className="text-danger">*</span></Label>
                      <CustomSelect
                        options={[
                          { label: "SMS", value: 1 },
                          { label: "Mail", value: 2 },
                          { label: "Call", value: 3 },
                          { label: "Whatsapp", value: 4 },
                        ]}
                        value={formik.values.verifiedVia}
                        onChange={(option) => formik.setFieldValue("verifiedVia", option || null)}
                        onBlur={() => formik.setFieldTouched("verifiedVia", true)}
                        className={`${
                          formik.touched.verifiedVia && formik.errors.verifiedVia
                            ? "is-invalid"
                            : ""
                        } hrms-select`}
                        classNamePrefix="hrms-select"
                      />
                      {formik.touched.verifiedVia && formik.errors.verifiedVia && (
                        <div className="text-danger">{formik.errors.verifiedVia}</div>
                      )}
                    </FormGroup>
                  </Col>
                </>
              )}

              <Col md={4}>
                <FormGroup>
                  <Label>Upload File (Experience Letter) <span className="text-danger">*</span></Label>
                  <Input key={fileKey} type="file" onChange={handleFileChange} />
                  {/* {selectedFile && <p className="text-success">Selected File: {selectedFile.name}</p>} */}
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger">{formik.errors.file}</div>
                  )}
                </FormGroup>
              </Col>

              <Col className="d-flex justify-content-end align-items-center">
                <Button type="submit" className="d-flex align-items-center btn button-add">
                  <i className="fas fa-plus me-2" /> Add
                </Button>
              </Col>
            </Row>
          </Form>

        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Row >
            <Col md={12}>
              <TableContainer
                columns={columns}
                data={employeeExpInformations?.length > 0 ? employeeExpInformations : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>

  );
};

export default WorkExperience;
